import { useState, useEffect } from 'react'
import { usePolicy } from '../../../contexts/PolicyContext'
import { useAuth } from '../../../contexts/AuthContext'
import { supabase } from '../../../shared/lib/supabase'

// ── Async enrichment pipeline (module-level so it persists after modal unmount) ──

export async function runEnrichmentPipeline(policies, clientData, assessmentContext) {
  for (const policy of policies) {
    try {
      await enrichSinglePolicy(policy, clientData, assessmentContext)
      await new Promise(r => setTimeout(r, 500))
    } catch (err) {
      console.error(`Enrichment failed for policy ${policy.id}:`, err)
      await supabase.from('client_policies').update({
        enrichment_status: 'enrichment_failed',
        enrichment_error: (err.message || 'Unknown error').slice(0, 255)
      }).eq('id', policy.id)
    }
  }
}

async function enrichSinglePolicy(policy, clientData, assessmentContext) {
  if (!policy.structured_content && !policy.content) {
    await supabase.from('client_policies').update({ enrichment_status: 'enriched_clean' }).eq('id', policy.id)
    return
  }

  const clientContext = {
    clientName: clientData?.name,
    industry: clientData?.industry,
    staffingSize: clientData?.staffing_size || clientData?.staffingSize,
    maturityLevel: assessmentContext?.maturity_level || null,
    overallScore: assessmentContext?.overall_score || null,
  }

  const frameworks = Array.isArray(clientData?.regulatory_frameworks || clientData?.regulatoryFrameworks)
    ? (clientData.regulatory_frameworks || clientData.regulatoryFrameworks)
    : []

  const requestBody = policy.structured_content
    ? { mode: 'tier3-enrich', structuredContent: policy.structured_content, domain: policy.domain, frameworks, clientContext }
    : { content: policy.content, domain: policy.domain, frameworks, clientContext }

  const res = await fetch('/api/ai/policy-enrich', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody)
  })

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}))
    throw new Error(errData.error || `HTTP ${res.status}`)
  }

  const result = await res.json()

  if (result.structured_content) {
    await supabase.from('client_policies').update({
      structured_content: result.structured_content,
      enrichment_status: result.enrichment_status || 'enriched_clean',
      gap_report: result.gap_report || []
    }).eq('id', policy.id)
  } else if (result.content) {
    await supabase.from('client_policies').update({
      content: result.content,
      enrichment_status: 'enriched_clean',
      gap_report: []
    }).eq('id', policy.id)
  }

  const contentForVersion = result.structured_content
    ? JSON.stringify(result.structured_content)
    : result.content || policy.content

  await supabase.from('policy_versions')
    .update({ content: contentForVersion, change_summary: 'Initial copy enriched for client context' })
    .eq('client_policy_id', policy.id)
    .eq('version_number', '1.0')
}

// ── Structured content preview (compact) ──

function CompactPreview({ sc }) {
  if (!sc) return null
  return (
    <div className="space-y-3">
      {sc.purpose && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Purpose</h4>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{sc.purpose}</p>
        </div>
      )}
      {sc.policy_statements?.slice(0, 2).map((ps, i) => (
        <div key={i}>
          <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">{ps.section_id}: {ps.title}</h4>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed line-clamp-3">{ps.content}</p>
        </div>
      ))}
      {(sc.policy_statements?.length || 0) > 2 && (
        <p className="text-xs text-gray-400 italic">+{sc.policy_statements.length - 2} more sections</p>
      )}
    </div>
  )
}

// ── Main modal ──

function PolicyLibrarySelectorModal({ clientId, clientData, existingPolicyDomains = [], onClose, onCopyComplete }) {
  const { templates } = usePolicy()
  const { user } = useAuth()

  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState(new Set())
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [copying, setCopying] = useState(false)

  // Filter to published, non-global org templates
  const orgTemplates = (templates || []).filter(t => !t.global_template && t.status === 'published')

  const filtered = orgTemplates.filter(t => {
    if (!search) return true
    const q = search.toLowerCase()
    return t.title?.toLowerCase().includes(q) || t.domain?.toLowerCase().includes(q)
  })

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleCopy = async () => {
    setCopying(true)
    const selectedTemplates = orgTemplates.filter(t => selected.has(t.id))
    const copiedPolicies = []
    let successCount = 0

    for (const template of selectedTemplates) {
      try {
        const { data: newPolicy, error } = await supabase
          .from('client_policies')
          .insert({
            client_id: clientId,
            template_id: template.id,
            title: template.title,
            domain: template.domain,
            content: template.content || '',
            structured_content: template.structured_content || null,
            framework_tags: template.framework_tags || [],
            status: 'draft',
            portal_visible: false,
            enrichment_status: 'pending',
            policy_version_number: '1.0'
          })
          .select()
          .single()

        if (error) throw error

        await supabase.from('policy_versions').insert({
          client_policy_id: newPolicy.id,
          content: template.content || '',
          change_summary: 'Initial copy from org library',
          created_by: user?.id,
          edit_source: 'org_copy',
          version_number: '1.0'
        })

        copiedPolicies.push({ ...newPolicy, structured_content: template.structured_content, content: template.content })
        successCount++
      } catch (err) {
        console.error(`Failed to copy template ${template.title}:`, err)
      }
    }

    // Close modal and notify parent
    onCopyComplete(successCount)

    // Fire-and-forget enrichment (runs after modal closes)
    if (copiedPolicies.length > 0) {
      // Fetch assessment context
      let assessmentContext = null
      try {
        const { data: assessment } = await supabase
          .from('assessments')
          .select('id')
          .eq('client_id', clientId)
          .eq('status', 'completed')
          .order('completed_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        if (assessment?.id) {
          const { data: scores } = await supabase
            .from('assessment_scores')
            .select('overall_score, maturity_level')
            .eq('assessment_id', assessment.id)
            .maybeSingle()
          assessmentContext = scores
        }
      } catch { /* continue without assessment context */ }

      runEnrichmentPipeline(copiedPolicies, clientData, assessmentContext)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Copy from Policy Library</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Select policies to copy to {clientData?.name}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by title or domain..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left: template list */}
          <div className="w-[55%] border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
            {orgTemplates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">No templates in your library yet.</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">Copy templates from the Global Templates tab in the Policy Library to get started.</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">No templates match your search.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {filtered.map(template => {
                  const isSelected = selected.has(template.id)
                  const isDuplicate = existingPolicyDomains.includes(template.domain)
                  return (
                    <div
                      key={template.id}
                      onClick={() => { toggleSelect(template.id); setPreviewTemplate(template) }}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-primary-50 dark:bg-primary-900/20 border-l-2 border-l-primary-500'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/30 border-l-2 border-l-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{template.title}</span>
                          {isDuplicate && (
                            <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Already copied</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{template.domain}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          {(template.framework_tags || []).slice(0, 2).map(tag => (
                            <span key={tag} className="text-xs px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">{tag}</span>
                          ))}
                          {(template.framework_tags || []).length > 2 && (
                            <span className="text-xs text-gray-400">+{template.framework_tags.length - 2}</span>
                          )}
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ml-1 ${
                            template.source_global_template_id && !template.org_edited
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {template.source_global_template_id && !template.org_edited ? 'Global' : 'Custom'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Right: preview */}
          <div className="w-[45%] overflow-y-auto p-6">
            {previewTemplate ? (
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">{previewTemplate.title}</h3>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs text-gray-500 dark:text-gray-400">{previewTemplate.domain}</span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    previewTemplate.status === 'published'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                  }`}>
                    {previewTemplate.status?.charAt(0).toUpperCase() + previewTemplate.status?.slice(1)}
                  </span>
                </div>
                {previewTemplate.framework_tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {previewTemplate.framework_tags.map(tag => (
                      <span key={tag} className="text-xs px-2 py-0.5 rounded bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">{tag}</span>
                    ))}
                  </div>
                )}
                {previewTemplate.structured_content ? (
                  <CompactPreview sc={previewTemplate.structured_content} />
                ) : previewTemplate.content ? (
                  <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {previewTemplate.content.slice(0, 500)}{previewTemplate.content.length > 500 ? '...' : ''}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No content available</p>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                <p className="text-sm text-gray-400 dark:text-gray-500">Select a policy to preview</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {selected.size > 0 ? `${selected.size} selected` : 'None selected'}
          </span>
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary">Cancel</button>
            <button
              onClick={handleCopy}
              disabled={selected.size === 0 || copying}
              className="btn-primary disabled:opacity-50 flex items-center gap-2"
            >
              {copying ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Copying...</>
              ) : (
                `Copy Selected (${selected.size})`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PolicyLibrarySelectorModal
